import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import vocalShiftLogo from "../../../../tvs-mobile/assets/brand/logo-white.png";
import nickPortrait from "../../../../tvs-mobile/assets/brand/nick-full.jpg";
import nickWelcome from "../../../../tvs-mobile/assets/brand/nick-welcome.jpg";
import goldMedallion from "./assets/app/gold-medallion.webp";
import onboardingGoal from "./assets/app/onboarding-goal.webp";
import onboardingIdentity from "./assets/app/onboarding-identity.webp";
import onboardingTime from "./assets/app/onboarding-time.webp";
import privacyPromise from "./assets/app/privacy-promise.webp";
import platinumMedallion from "./assets/app/platinum-medallion.webp";
import silverMedallion from "./assets/app/silver-medallion.webp";
import todayScreen from "./assets/app/today.webp";

const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL || "hello@thevocalshift.app";
const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL as string | undefined;
const PLAY_STORE_URL = import.meta.env.VITE_PLAY_STORE_URL as string | undefined;

const silverBenefits = [
  "Your Vocal Blueprint™",
  "Vocal Intelligence™",
  "The Vocal Gym™ and Vocal Dojo™",
  "The Vocal Elevator™",
  "Daily Performance System™",
  "Championsphere™",
];

const goldBenefits = [
  "Everything in Silver",
  "Advanced Vocal Intelligence™",
  "Premium Learning Studio™",
  "3 live Masterclasses with Nick each year",
  "3 Guest Spotlight conversations each year",
  "Creative Village™",
];

const platinumBenefits = [
  "A high-touch path shaped around your ambitions",
  "Direct conversation before membership",
  "Private and live experiences tailored to fit",
  "Priority relationship with the Vocal Shift team",
];

function ArrowIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18">
      <path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
    </svg>
  );
}

function Mark() {
  return (
    <Link className="brand" to="/" aria-label="The Vocal Shift home">
      <img src={vocalShiftLogo} alt="" width="428" height="99" />
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className={location.pathname === "/" ? "site-header site-header--split" : "site-header"}>
      <Mark />
      <button className="menu-button" type="button" aria-expanded={open} aria-controls="site-nav" onClick={() => setOpen((value) => !value)}>
        <span>{open ? "Close" : "Menu"}</span>
      </button>
      <nav className={open ? "site-nav site-nav--open" : "site-nav"} id="site-nav" aria-label="Primary navigation">
        <NavLink to="/#method">The method</NavLink>
        <NavLink to="/plans">Plans</NavLink>
        <NavLink to="/support">Support</NavLink>
        <Link className="nav-cta" to="/plans">Find your path <ArrowIcon /></Link>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="footer-lead">
        <Mark />
        <p>Your voice is personal. Your practice should be too.</p>
      </div>
      <div className="footer-links">
        <div><span>Explore</span><Link to="/plans">Plans</Link><Link to="/support">Support</Link></div>
        <div><span>Legal</span><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></div>
        <div><span>Contact</span><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div>
      </div>
      <div className="footer-bottom">
        <span>© {new Date().getFullYear()} Nicholas Cooper Brands, Inc.</span>
        <span>Personalized vocal coaching</span>
      </div>
    </footer>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      window.requestAnimationFrame(() => document.querySelector(location.hash)?.scrollIntoView());
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname, location.hash]);

  return <><Header /><main>{children}</main><Footer /></>;
}

function StoreActions({ compact = false }: { compact?: boolean }) {
  if (!APP_STORE_URL && !PLAY_STORE_URL) {
    return <Link className={compact ? "button button--light" : "button"} to="/plans">Explore membership <ArrowIcon /></Link>;
  }
  return (
    <div className="store-actions">
      {APP_STORE_URL ? <a className="button" href={APP_STORE_URL}>Download for iPhone <ArrowIcon /></a> : null}
      {PLAY_STORE_URL ? <a className="button button--outline" href={PLAY_STORE_URL}>Get it on Android <ArrowIcon /></a> : null}
    </div>
  );
}

function AppScreen({ src, alt, className = "" }: { src: string; alt: string; className?: string }) {
  return (
    <div className={`app-screen ${className}`}>
      <span className="app-screen__speaker" aria-hidden="true" />
      <img src={src} alt={alt} loading="lazy" />
    </div>
  );
}

function HomePage() {
  return (
    <PageShell>
      <section className="hero">
        <div className="hero__copy">
          <p className="hero__cue">Personalized vocal coaching</p>
          <h1>Build the voice that changes everything.</h1>
          <p className="hero__intro">Expert instruction becomes a daily practice shaped around your voice, your goals, and the person you are becoming.</p>
          <div className="hero__actions"><StoreActions /><a className="text-link" href="#method">See how it works <span>↓</span></a></div>
          <p className="hero__note">Private on-device voice mapping. A practice you can return to every day.</p>
        </div>
        <div className="hero__portrait">
          <img src={nickPortrait} alt="Nicholas Cooper, founder and vocal coach of The Vocal Shift" />
          <p><span>Built with</span> Nicholas Cooper</p>
        </div>
        <div className="hero__rail" aria-hidden="true"><span>FOUNDATION</span><span>BLEND</span><span>PRESENCE</span></div>
      </section>

      <section className="belief">
        <p>The voice is not a feature to install.</p>
        <h2>It is an instrument to understand.</h2>
        <p className="belief__body">The Vocal Shift meets you where you are, then gives you the structure to keep moving—with less strain, more awareness, and a clear next step.</p>
      </section>

      <section className="method" id="method">
        <div className="section-heading">
          <p>One practice. Built around you.</p>
          <h2>From first signal to lasting shift.</h2>
        </div>
        <div className="method__steps">
          <article>
            <div className="method__screen"><AppScreen src={onboardingIdentity} alt="The Vocal Shift onboarding screen asking who you are becoming" /></div>
            <div className="method__copy"><span>01</span><h3>Name your direction</h3><p>Start with the person you are becoming, so your practice has somewhere meaningful to lead.</p></div>
          </article>
          <article>
            <div className="method__screen"><AppScreen src={onboardingGoal} alt="The Vocal Shift onboarding screen asking what would make the app life-changing" /></div>
            <div className="method__copy"><span>02</span><h3>Set the shift</h3><p>Tell us what would change everything. Your goals shape what the app brings forward.</p></div>
          </article>
          <article>
            <div className="method__screen"><AppScreen src={onboardingTime} alt="The Vocal Shift onboarding screen asking how much time you can practice" /></div>
            <div className="method__copy"><span>03</span><h3>Make it sustainable</h3><p>Choose the time you really have. The practice adapts to your life, not the other way around.</p></div>
          </article>
        </div>
      </section>

      <section className="practice">
        <div className="practice__visual">
          <div className="practice__halo" aria-hidden="true" />
          <AppScreen src={todayScreen} alt="The Vocal Shift Today screen with a personalized ten-minute session" className="app-screen--today" />
        </div>
        <div className="practice__copy">
          <p>Practice, not pressure.</p>
          <h2>Know what to do today.</h2>
          <p>The hardest part of growth is often deciding what comes next. Your daily session keeps the work focused, sized to your schedule, and connected to a larger path.</p>
          <ul>
            <li><span>01</span> Personalized daily routines</li>
            <li><span>02</span> Structured lesson library</li>
            <li><span>03</span> Reflections and progress</li>
            <li><span>04</span> Coaching in context</li>
          </ul>
        </div>
      </section>

      <section className="privacy-promise">
        <div className="privacy-promise__visual">
          <AppScreen src={privacyPromise} alt="The Vocal Shift privacy promise shown during onboarding" className="app-screen--privacy" />
        </div>
        <div className="privacy-promise__copy">
          <p>Privacy by design</p>
          <h2>This is not an audition.</h2>
          <p>We are not judging your voice. Live analysis is designed to stay on your device, and nothing is posted publicly. You decide when to record and share a short clip for coaching.</p>
          <ul><li>Private &amp; secure</li><li>No public posting</li><li>For personalization only</li><li>Retake anytime</li></ul>
          <Link to="/privacy">Read our privacy policy <ArrowIcon /></Link>
        </div>
      </section>

      <section className="coach">
        <div className="coach__image"><img src={nickWelcome} alt="Nicholas Cooper in the studio" /></div>
        <div className="coach__copy">
          <p>Meet the coach behind the method</p>
          <h2>Extraordinary is built.</h2>
          <blockquote>“Your voice changes first. Your life follows.”</blockquote>
          <p>Nicholas Cooper has spent his career helping voices move with greater freedom, clarity, and presence. The Vocal Shift turns that depth of instruction into a practice you can carry every day.</p>
          <Link className="text-link text-link--light" to="/plans">Choose your path <ArrowIcon /></Link>
        </div>
      </section>

      <PlansPreview />
      <section className="closing"><p>Begin where you are.</p><h2>Your next shift starts with today’s voice.</h2><StoreActions compact /></section>
    </PageShell>
  );
}

type PlanProps = { name: string; subtitle: string; price?: string; benefits: string[]; tone: "silver" | "gold" | "platinum"; preview?: boolean };

function Plan({ name, subtitle, price, benefits, tone, preview = false }: PlanProps) {
  const platinum = tone === "platinum";
  const medallion = tone === "silver" ? silverMedallion : tone === "gold" ? goldMedallion : platinumMedallion;
  const summary = platinum ? "A high-touch relationship shaped around your ambitions, access, and next chapter." : tone === "gold" ? "Turn talent into traction. Build confidence, brand clarity, leadership, and real opportunity." : "Understand your voice. Build daily habits. Grow confidence every time you speak, sing, or lead.";
  const closing = platinum ? "The right relationship begins with a conversation." : tone === "gold" ? "Your voice changes first. Your life follows." : "Extraordinary is built.";
  return (
    <article className={`plan plan--${tone}`}>
      <div className="plan__body">
        <div className="plan__left">
          <div className="plan__identity">
            <img src={medallion} alt="" width="80" height="80" loading="lazy" />
            <div><span className="plan__name">{name}</span><h3>{subtitle}</h3></div>
          </div>
          <p className="plan__price">{price ? <><strong>{price}</strong><span>/ month</span></> : <strong>By conversation</strong>}</p>
          {tone === "gold" ? <em className="plan__popular">Most popular</em> : null}
          <p className="plan__summary">{summary}</p>
        </div>
        <div className="plan__divider" aria-hidden="true" />
        <div className="plan__right">
          <ul>{benefits.map((benefit) => <li key={benefit}><span>✓</span>{benefit}</li>)}</ul>
          {platinum ? (
            <a className="plan__action" href={`mailto:${CONTACT_EMAIL}?subject=Platinum membership conversation`}>Talk to us <ArrowIcon /></a>
          ) : (
            <a className="plan__action" href={preview ? "/plans#membership-note" : "#membership-note"}>Start in the app <ArrowIcon /></a>
          )}
        </div>
      </div>
      <p className="plan__closing">{closing}</p>
    </article>
  );
}

function PlansPreview() {
  return (
    <section className="plans-preview">
      <div className="section-heading section-heading--center"><p>Three paths, one standard</p><h2>Choose the room you are ready to enter.</h2></div>
      <div className="plans-grid">
        <Plan name="Silver" subtitle="Build your foundation." price="$49.99" benefits={silverBenefits} tone="silver" preview />
        <Plan name="Gold" subtitle="Develop your signature." price="$99.99" benefits={goldBenefits} tone="gold" preview />
        <Plan name="Platinum" subtitle="Shape what comes next." benefits={platinumBenefits} tone="platinum" preview />
      </div>
      <Link className="all-plans" to="/plans">Compare every benefit <ArrowIcon /></Link>
    </section>
  );
}

function PlansPage() {
  return (
    <PageShell>
      <section className="page-hero page-hero--plans"><p>Membership</p><h1>Choose the path that fits where you are now.</h1><p>Every level helps you grow your voice, your confidence, and your future.</p></section>
      <section className="plans-full">
        <div className="plans-grid">
          <Plan name="Silver" subtitle="Build your foundation." price="$49.99" benefits={silverBenefits} tone="silver" />
          <Plan name="Gold" subtitle="Develop your signature." price="$99.99" benefits={goldBenefits} tone="gold" />
          <Plan name="Platinum" subtitle="Shape what comes next." benefits={platinumBenefits} tone="platinum" />
        </div>
        <div className="membership-note" id="membership-note">
          <h2>Silver and Gold live in the app.</h2>
          <p>Download The Vocal Shift, complete your voice profile, and choose Silver or Gold during onboarding. Your localized price is shown by Apple or Google before purchase.</p>
          <StoreActions />
        </div>
      </section>
      <section className="faq">
        <div><p>Membership questions</p><h2>The details, in a clear voice.</h2></div>
        <div className="faq__items">
          <details><summary>Can I cancel Silver or Gold?</summary><p>Yes. Both are monthly auto-renewing memberships that you can manage or cancel through the store account used to subscribe.</p></details>
          <details><summary>Is Platinum an in-app subscription?</summary><p>No. Platinum begins with a conversation so we can understand the relationship and level of support you are looking for. It is intentionally separate from the app’s Silver and Gold paywall.</p></details>
          <details><summary>Does the app continuously upload my microphone audio?</summary><p>No. Live pitch and voice mapping run on your device. A short clip is uploaded only when you deliberately choose to share one for coaching.</p></details>
          <details><summary>Can I restore an existing membership?</summary><p>Yes. Use Restore Purchases on the membership screen while signed into the same Apple or Google account used for the original purchase.</p></details>
        </div>
      </section>
    </PageShell>
  );
}

function LegalLayout({ title, updated, children }: { title: string; updated: string; children: ReactNode }) {
  return (
    <PageShell>
      <section className="legal-hero"><p>The Vocal Shift</p><h1>{title}</h1><span>Last updated {updated}</span></section>
      <article className="legal-document">{children}</article>
    </PageShell>
  );
}

function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="July 16, 2026">
      <p className="legal-lead">This Privacy Policy explains how Nicholas Cooper Brands, Inc. (“The Vocal Shift,” “we,” “us,” or “our”) collects, uses, shares, and protects information when you use The Vocal Shift mobile application, website, and related services.</p>
      <h2>1. Information we collect</h2>
      <h3>Information you provide</h3>
      <p>We collect information you provide when you create an account, complete onboarding, contact support, participate in coaching, or otherwise use the service. This may include your name, email address, authentication details, goals, practice preferences, voice-profile responses, reflections, messages, and support communications.</p>
      <h3>Voice and microphone information</h3>
      <p>Live pitch and vocal-signal analysis is designed to run on your device. We do not continuously send microphone audio to our servers for analysis. If you deliberately record and submit a short voice clip for coaching or another feature, we process that clip to provide the feature you requested.</p>
      <h3>Usage and device information</h3>
      <p>We may collect device type, operating system, app version, language, time zone, diagnostic logs, feature interactions, approximate region derived from network information, and similar data needed to secure, maintain, and improve the service.</p>
      <h3>Purchase information</h3>
      <p>Silver and Gold purchases are processed by Apple or Google and managed through our subscription service provider. We receive subscription status, product, transaction identifiers, and related entitlement information. We do not receive your full payment-card number.</p>
      <h2>2. How we use information</h2>
      <p>We use information to provide and personalize your practice; authenticate accounts; maintain membership access; deliver lessons, live experiences, coaching, and support; remember progress; secure and troubleshoot the service; communicate service updates; comply with law; and improve our products.</p>
      <h2>3. How we share information</h2>
      <p>We may share information with service providers that support cloud hosting, authentication, database operations, subscriptions, video delivery, notifications, customer support, analytics, and AI-assisted coaching. These providers may process information only as needed to perform services for us and subject to their contractual obligations.</p>
      <p>We may also disclose information when required by law; to protect users, the public, our rights, or the security of the service; in connection with a merger, financing, acquisition, or sale of assets; or with your direction or consent. We do not sell personal information for money.</p>
      <h2>4. AI-assisted coaching</h2>
      <p>When you use conversational coaching, the text and short clips you choose to submit may be processed by automated systems and service providers to generate a response. Do not submit information you do not want processed for that purpose. Automated coaching can be incomplete or inaccurate and is not medical, therapeutic, or emergency advice.</p>
      <h2>5. Data retention</h2>
      <p>We retain information for as long as reasonably necessary to provide the service, maintain legitimate business records, resolve disputes, enforce agreements, and meet legal obligations. Retention periods vary by the nature of the information. We may retain de-identified information that can no longer reasonably identify you.</p>
      <h2>6. Security</h2>
      <p>We use administrative, technical, and organizational safeguards designed to protect information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.</p>
      <h2>7. Your choices and rights</h2>
      <p>You may update certain account information in the app and manage device permissions through your operating system. Depending on where you live, you may have rights to access, correct, delete, restrict, or obtain a copy of personal information, or to object to certain processing. To make a request, contact us using the address below. We may verify your identity before completing a request.</p>
      <h2>8. Children</h2>
      <p>The service is intended for adults and is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided personal information, contact us so we can take appropriate action.</p>
      <h2>9. International use</h2>
      <p>Your information may be processed in countries other than where you live. Where required, we use appropriate safeguards for international transfers.</p>
      <h2>10. Changes to this policy</h2>
      <p>We may update this policy. We will post the updated version here, change the “Last updated” date, and provide additional notice when required by law.</p>
      <h2>11. Contact us</h2>
      <p>Questions or privacy requests can be sent to <a href={`mailto:${CONTACT_EMAIL}?subject=Privacy request`}>{CONTACT_EMAIL}</a>.</p>
    </LegalLayout>
  );
}

function TermsPage() {
  return (
    <LegalLayout title="Terms of Use" updated="July 16, 2026">
      <p className="legal-lead">These Terms of Use (“Terms”) govern your use of The Vocal Shift mobile application, website, and related services provided by Nicholas Cooper Brands, Inc. By using the service, you agree to these Terms.</p>
      <h2>1. Eligibility and accounts</h2>
      <p>You must be at least 18 years old, or the age of legal majority where you live, to create an account. You agree to provide accurate information, keep your credentials secure, and notify us of suspected unauthorized access. You are responsible for activity under your account.</p>
      <h2>2. The service</h2>
      <p>The Vocal Shift provides educational vocal instruction, guided practice, voice-profile tools, lessons, reflections, live experiences, and automated coaching features. Features may change as the service evolves. We may suspend features for maintenance, security, or operational reasons.</p>
      <h2>3. Memberships and billing</h2>
      <p>Silver and Gold are monthly auto-renewing memberships purchased through Apple App Store or Google Play. Prices and applicable taxes are shown by the store before purchase. Your store account is charged at confirmation and at each renewal unless you cancel through that store before the renewal date. Store terms and refund rules apply. The app includes a Restore Purchases option for eligible purchases.</p>
      <p>Platinum is not an in-app subscription. It begins with a separate conversation, and any resulting services, price, payment terms, or additional agreement will be disclosed before you commit.</p>
      <h2>4. Educational purpose and wellbeing</h2>
      <p>The service is for education and practice. It is not medical care, diagnosis, treatment, speech therapy, mental-health care, or emergency assistance. Stop an exercise if you experience pain, significant discomfort, dizziness, or loss of voice, and seek advice from an appropriate qualified professional.</p>
      <h2>5. Automated features</h2>
      <p>Automated coaching and generated responses may be incomplete, inaccurate, or unsuitable for your circumstances. Use your judgment and do not rely on the service for medical, legal, financial, safety-critical, or emergency decisions.</p>
      <h2>6. Acceptable use</h2>
      <p>You may not misuse the service; break the law; infringe another person’s rights; upload harmful code; interfere with security or operation; access another person’s account; scrape or extract content at scale; reverse engineer the service except where law permits; use the service to train competing models or products; or harass, threaten, or exploit others.</p>
      <h2>7. Your content</h2>
      <p>You retain ownership of content you submit. You grant us a limited, worldwide license to host, process, reproduce, and transmit that content only as needed to operate, secure, and improve the service and provide features you request. You represent that you have the rights needed to submit the content.</p>
      <h2>8. Our intellectual property</h2>
      <p>The service, lessons, coaching materials, designs, trademarks, software, and other content supplied by us are owned by or licensed to Nicholas Cooper Brands, Inc. These Terms grant you a personal, limited, non-exclusive, non-transferable, revocable right to use the service for its intended purpose. No other rights are granted.</p>
      <h2>9. Third-party services</h2>
      <p>The service may rely on or link to third-party platforms, app stores, video providers, and other services. Their terms and privacy practices govern your use of those services. We are not responsible for third-party services outside our control.</p>
      <h2>10. Suspension and termination</h2>
      <p>You may stop using the service at any time. We may suspend or terminate access if you materially violate these Terms, create risk or legal exposure, fail to pay applicable fees, or if continued operation is no longer commercially reasonable. Provisions that by their nature should survive termination will survive.</p>
      <h2>11. Disclaimers</h2>
      <p>To the maximum extent permitted by law, the service is provided “as is” and “as available.” We disclaim warranties of merchantability, fitness for a particular purpose, non-infringement, and uninterrupted or error-free operation. We do not guarantee a particular vocal, professional, creative, or personal outcome.</p>
      <h2>12. Limitation of liability</h2>
      <p>To the maximum extent permitted by law, Nicholas Cooper Brands, Inc. and its affiliates will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or loss of data, profits, goodwill, or opportunity. Our aggregate liability arising from the service will not exceed the greater of the amount you paid us for the service during the preceding 12 months or US $100. Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.</p>
      <h2>13. Changes</h2>
      <p>We may update these Terms. If changes are material, we will provide notice as required by law. Continued use after the effective date means you accept the updated Terms.</p>
      <h2>14. Contact</h2>
      <p>Questions about these Terms can be sent to <a href={`mailto:${CONTACT_EMAIL}?subject=Terms question`}>{CONTACT_EMAIL}</a>.</p>
    </LegalLayout>
  );
}

function SupportPage() {
  return (
    <PageShell>
      <section className="page-hero"><p>Support</p><h1>Let’s get you back to your practice.</h1><p>Tell us what is happening and include the email connected to your account.</p></section>
      <section className="support-grid">
        <article><span>Membership</span><h2>Purchases and access</h2><p>For missing access, first use Restore Purchases in the app while signed into the same Apple or Google account used to subscribe.</p><a href={`mailto:${CONTACT_EMAIL}?subject=Membership support`}>Get membership help <ArrowIcon /></a></article>
        <article><span>Product</span><h2>App and practice help</h2><p>Include your device model, operating-system version, app version, and a short description of the last step that worked.</p><a href={`mailto:${CONTACT_EMAIL}?subject=App support`}>Contact product support <ArrowIcon /></a></article>
        <article className="support-grid__platinum"><span>Platinum</span><h2>Start a conversation</h2><p>Tell us about your voice, where you are headed, and the level of relationship you are looking for.</p><a href={`mailto:${CONTACT_EMAIL}?subject=Platinum membership conversation`}>Talk to us <ArrowIcon /></a></article>
      </section>
    </PageShell>
  );
}

function NotFoundPage() {
  return <PageShell><section className="not-found"><p>404</p><h1>That note isn’t in the arrangement.</h1><Link className="button" to="/">Return home <ArrowIcon /></Link></section></PageShell>;
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
